import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";
import { Base64 } from "js-base64";
import { Fragment, useMemo } from "react";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import { default as TruncatedText } from "../../../components/TruncatedText";
import { useViewport } from "../../../context/viewport";
import { breakpoints } from "../../../utils/constants";
import { PaymentTransaction } from "../../../utils/types";
import { useHntMarket } from "../../../utils/useHntMarket";
import { renderDate } from "../../Map/HotspotDetails/HotspotInfoTab";

interface Props {
  transaction: PaymentTransaction | null;
  open: boolean;
  onClose: () => void;
}

const PaymentTransactionSidebar: React.FC<Props> = ({
  transaction,
  open,
  onClose
}) => {
  const { displayInCurrency } = useHntMarket();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);

  const truncatedLength = useMemo(() => (isMobile ? 10 : 30), [isMobile]);

  const renderContent = useMemo(() => {
    if (!transaction) return null;

    return (
      <div className="flex-1 py-6 divide-y divide-gray-200 dark:divide-gray-500 overflow-y-auto">
        <div className="px-4 sm:px-6 space-y-6">
          <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                Amount
              </dt>

              <dd className="flex items-baseline mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                {displayInCurrency(transaction.total_amount, {
                  needsDivision: true,
                  withHntSuffix: false
                })}
              </dd>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Date</h3>
            <div className="mt-2">{renderDate(transaction.time)}</div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">
              Transaction ID
            </h3>
            <TruncatedText
              className="mt-2"
              text={transaction.hash}
              label="transaction ID"
              truncateLength={truncatedLength}
            />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Sender</h3>
            <TruncatedText
              className="mt-2"
              text={transaction.payer}
              label="sender wallet address"
              truncateLength={truncatedLength}
            />
            <DisplayLabels contextWalletAddresses={[transaction.payer]} />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 flex items-center justify-between">
              <span>
                Recipient{transaction.payments.length === 1 ? "" : "s"} (
                {transaction.payments.length})
              </span>
              <span>Amounts</span>
            </h3>
            <dl className="mt-2 border-t border-gray-200 divide-y divide-gray-200">
              {transaction.payments.map(({ payee, memo, amount }) => (
                <div
                  key={payee}
                  className="py-3 flex items-start justify-between text-sm font-medium"
                >
                  <dt>
                    <TruncatedText
                      text={payee}
                      label="recipient wallet address"
                      truncateLength={truncatedLength}
                    />
                    {memo !== DEFAULT_PAYMENT_MEMO && (
                      <div>
                        <span className="text-gray-400 italic">Memo: </span>
                        {Base64.decode(memo)}
                      </div>
                    )}
                    <DisplayLabels contextWalletAddresses={[payee]} />
                  </dt>
                  <dd className="text-md font-semibold text-gray-900 dark:text-white">
                    {displayInCurrency(amount, {
                      needsDivision: true,
                      withHntSuffix: false
                    })}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    );
  }, [displayInCurrency, transaction, truncatedLength]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={onClose}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <div className="absolute inset-0" onClick={onClose} />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-md dark:text-white">
                <div className="max-w-3xl xl:max-w-6xl h-full">
                  <div className="h-full flex flex-col bg-white shadow-xl dark:bg-gray-900">
                    <div className="flex-1 h-0 flex flex-col">
                      <div className="flex-none py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-medium text-white">
                            Payment transaction
                          </Dialog.Title>
                          <div className="ml-3 h-7 flex items-center">
                            <button
                              type="button"
                              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                              onClick={onClose}
                            >
                              <span className="sr-only">Close panel</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {renderContent}
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PaymentTransactionSidebar;

//
// Utils
//

export const DEFAULT_PAYMENT_MEMO = "AAAAAAAAAAA=";
